// src/components/kuestion/solo/SoloProgress.jsx
// Own strengths (§7), the mocks chart, and the full sitting history with the
// verdict each sitting was actually judged against (v4 §6).
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSoloData, verdictOf } from './soloData';
import { Loading, RuleHead, ModuleRow, Note } from '../shared/Bits';
import { fmtDate, fmtClock } from '../shared/format';
import MocksChart from './MocksChart';
import { kq } from '../../../config/routes';

export const Verdict = ({ sitting }) => {
  const v = verdictOf(sitting);
  if (v === null) return <span className="kqc-verdict is-none" title="No pass mark on this paper">No mark</span>;
  return <span className={`kqc-verdict is-${v}`}>{v === 'pass' ? 'Pass' : 'Fail'}</span>;
};

export default function SoloProgress() {
  const navigate = useNavigate();
  const { history, strengths } = useSoloData();
  const modules = strengths?.modules || [];
  const min = strengths?.min_questions_for_strength;

  return (
    <div className="kqc-page kqc-page--mid">
      <div className="kqc-pagehead" style={{ marginBottom: 18 }}>
        <div>
          <div className="kqc-kicker" style={{ marginBottom: 8 }}>Progress</div>
          <h1 className="kqc-h1">Where you stand</h1>
        </div>
        <span className="kqc-spacer" />
        <button type="button" className="btn btn-primary" onClick={() => navigate(kq('exam'))}>Sit a mock</button>
      </div>

      <div className="kqc-cols">
        <div className="is-main">
          <RuleHead title="Domain strengths" />
          {strengths === null ? <Loading label="Loading" /> : modules.length === 0 ? (
            <div className="kqc-empty">Strengths appear once you have sat a few papers.</div>
          ) : (
            <div className="kqc-list">
              {modules.map((m) => <ModuleRow key={m.module} m={m} min={min} stacked />)}
            </div>
          )}
          <Note style={{ marginTop: 16 }}>Below {min ?? 'a few'} attempted questions we report insufficient data rather than a percentage. Two out of three is not 67% worth of anything, and a figure like that would send you revising the wrong thing.</Note>
        </div>
        <div className="is-side" style={{ maxWidth: 460 }}>
          <RuleHead title="Your last five mocks" />
          {history === null ? <Loading label="Loading" /> : history.length === 0 ? <div className="kqc-empty">No sittings yet.</div>
            : <MocksChart sittings={history.slice(0, 5).reverse()} />}
        </div>
      </div>

      <div style={{ marginTop: 'clamp(24px,3vw,34px)' }}>
        <RuleHead title="Every sitting" />
        {history === null ? <Loading label="Loading history" /> : history.length === 0 ? (
          <div className="kqc-empty">No sittings yet.</div>
        ) : (
          <div className="table-wrap">
            <table className="table" style={{ minWidth: 640 }}>
              <thead><tr><th>Exam</th><th>Sat</th><th>Time</th><th>Correct</th><th>Score</th><th>Pass mark</th><th>Verdict</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="kqc-mono">{h.exam_name}</td>
                    <td>{fmtDate(h.at)}</td>
                    <td>{fmtClock(h.time_taken)}</td>
                    <td>{h.scored} / {h.total}</td>
                    <td><strong className={h.passed === false ? 'is-danger' : ''}>{Math.round(h.pct)}%</strong></td>
                    <td className="kqc-muted">{h.pass_percentage != null ? `${h.pass_percentage}%` : '—'}</td>
                    <td><Verdict sitting={h} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
